import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { joinWaitlist, API, trackView } from "@/lib/api";
import { Check, Copy, PartyPopper, Share2, Loader2, Download } from "lucide-react";
import { motion } from "framer-motion";
import { TESTFLIGHT_URL, GOOGLE_PLAY_URL, isTestFlightLive } from "@/lib/testflight";

export default function WaitlistDialog({ open, onOpenChange, refCode }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const shareLink = result
    ? `${window.location.origin}/?ref=${result.referral_code}`
    : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    setLoading(true);
    try {
      const data = await joinWaitlist({
        name,
        email,
        referral_code: refCode || null,
        city: city || null,
      });
      setResult(data);
      // Remember this user for the returning-visitor pill on future homepage visits
      try {
        localStorage.setItem(
          "gm_waitlist",
          JSON.stringify({
            name,
            email: email.toLowerCase().trim(),
            referral_code: data.referral_code,
            position: data.position,
            saved_at: new Date().toISOString(),
          })
        );
      } catch (_) {}
      if (!data.already_registered) trackView(window.location.pathname, "waitlist_signup");
      if (data.already_registered) {
        toast.info("You're already on the list — here's your invite link!");
      } else {
        toast.success("Welcome to GateMate! Check your inbox.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    toast.success("Invite link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "GateMate — Step In. Link Up.",
          text: "Follow GateMate — the live-event social network for DFW and beyond.",
          url: shareLink,
        });
      } catch (_) {}
    } else {
      copy();
    }
  };

  const downloadCard = async (fmt = "og") => {
    try {
      const suffix = fmt === "story" ? "?format=story" : "";
      const res = await fetch(`${API}/share-card/${result.referral_code}.png${suffix}`);
      if (!res.ok) throw new Error("bad response");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gatemate-${result.referral_code}-${fmt}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(fmt === "story" ? "Story card saved — post it!" : "Share card saved!");
    } catch (_) {
      toast.error("Couldn't generate the image. Try again.");
    }
  };

  const reset = () => {
    setResult(null);
    setName("");
    setEmail("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 300); }}>
      <DialogContent
        data-testid="waitlist-dialog"
        className="bg-[#0c1714] border border-white/10 text-white sm:max-w-md glass-violet"
      >
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-bold">Stay updated with GateMate</DialogTitle>
              <DialogDescription className="text-zinc-400">
                GateMate is live on iOS, Android and the web. Subscribe for city updates, product news and venue drops near you.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="wl-name" className="text-zinc-300">Name</Label>
                <Input
                  id="wl-name"
                  data-testid="waitlist-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus-visible:ring-neon-violet"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wl-email" className="text-zinc-300">Email</Label>
                <Input
                  id="wl-email"
                  data-testid="waitlist-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus-visible:ring-neon-violet"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wl-city" className="text-zinc-300">
                  Which city are you in? <span className="text-zinc-500 text-xs">(venue partnerships start in DFW)</span>
                </Label>
                <select
                  id="wl-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  data-testid="waitlist-city-select"
                  className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-neon-violet/40"
                >
                  <option value="" className="bg-[#0b1613]">Select your city…</option>
                  <optgroup label="Dallas–Fort Worth (venue partners first)">
                    <option value="dallas" className="bg-[#0b1613]">Dallas</option>
                    <option value="fort-worth" className="bg-[#0b1613]">Fort Worth</option>
                    <option value="plano" className="bg-[#0b1613]">Plano</option>
                    <option value="frisco" className="bg-[#0b1613]">Frisco</option>
                    <option value="arlington" className="bg-[#0b1613]">Arlington</option>
                    <option value="irving" className="bg-[#0b1613]">Irving</option>
                    <option value="richardson" className="bg-[#0b1613]">Richardson</option>
                    <option value="grapevine" className="bg-[#0b1613]">Grapevine</option>
                    <option value="mckinney" className="bg-[#0b1613]">McKinney</option>
                    <option value="denton" className="bg-[#0b1613]">Denton</option>
                    <option value="addison" className="bg-[#0b1613]">Addison</option>
                  </optgroup>
                  <optgroup label="Other markets">
                    <option value="other" className="bg-[#0b1613]">I'm somewhere else</option>
                  </optgroup>
                </select>
                {city === "other" && (
                  <p className="text-xs text-zinc-400" data-testid="waitlist-city-other-note">
                    The app already works anywhere — we'll bring partner venues to your city as demand grows.
                  </p>
                )}
              </div>
              {refCode && (
                <p className="text-xs text-neon-cyan">Invited with code {refCode} — nice.</p>
              )}
              <p className="text-[11px] text-zinc-500 leading-relaxed" data-testid="waitlist-age-gate">
                By subscribing you confirm you are <strong className="text-zinc-300">18 years of age or older</strong> and agree to our{" "}
                <a href="/terms" className="underline hover:text-white">Terms</a> and{" "}
                <a href="/privacy-policy" className="underline hover:text-white">Privacy Policy</a>.
              </p>
              <Button
                type="submit"
                data-testid="waitlist-submit-button"
                disabled={loading}
                className="w-full rounded-full bg-gradient-to-r from-neon-violet to-neon-cyan text-white font-semibold h-12 btn-glow border-0 hover:opacity-95"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Subscribing…</> : "Subscribe to updates"}
              </Button>
            </form>
          </>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="waitlist-success">
            <DialogHeader>
              <div className="mx-auto mb-3 grid place-items-center w-14 h-14 rounded-full bg-gradient-to-br from-neon-violet to-neon-cyan">
                <PartyPopper className="w-7 h-7 text-white" />
              </div>
              <DialogTitle className="font-display text-3xl font-bold text-center">You're in.</DialogTitle>
              <DialogDescription className="text-zinc-400 text-center">
                {isTestFlightLive()
                  ? "GateMate is live on the App Store and Google Play. Grab it, then share your link so your crew's there when you are."
                  : "We'll email you the moment the iOS app lands in DFW. Want your crew there on night one? Share your link."}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="rounded-xl overflow-hidden border border-white/10">
                <img
                  data-testid="waitlist-share-card"
                  src={`${API}/share-card/${result.referral_code}.png`}
                  alt="I'm in for night one — GateMate"
                  className="w-full block"
                />
              </div>
              <div className="glass rounded-xl p-4">
                <p className="overline mb-2">Your invite link</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-zinc-200 truncate" data-testid="waitlist-share-link">{shareLink}</code>
                  <Button size="icon" variant="ghost" onClick={copy} data-testid="waitlist-copy-button" className="text-neon-cyan hover:bg-white/10">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Button
                  onClick={() => downloadCard("og")}
                  data-testid="waitlist-download-card-button"
                  variant="outline"
                  className="rounded-full h-12 glass border-white/10 text-white hover:bg-white/10 px-2"
                >
                  <Download className="w-4 h-4 mr-1.5" /> Post
                </Button>
                <Button
                  onClick={() => downloadCard("story")}
                  data-testid="waitlist-download-story-button"
                  variant="outline"
                  className="rounded-full h-12 glass border-white/10 text-white hover:bg-white/10 px-2"
                >
                  <Download className="w-4 h-4 mr-1.5" /> Story
                </Button>
                <Button
                  onClick={share}
                  data-testid="waitlist-share-button"
                  className="rounded-full h-12 bg-gradient-to-r from-neon-violet to-neon-cyan text-white font-semibold btn-glow border-0 px-2"
                >
                  <Share2 className="w-4 h-4 mr-1.5" /> Share
                </Button>
              </div>
              <div className="flex items-center justify-center gap-4 pt-1">
                {TESTFLIGHT_URL ? (
                  <a
                    href={TESTFLIGHT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="waitlist-testflight-link"
                    className="text-xs text-zinc-500 hover:text-neon-cyan transition-colors"
                  >
                    {isTestFlightLive()
                      ? "Get GateMate on the App Store →"
                      : "Can't wait? Get the iOS app on TestFlight →"}
                  </a>
                ) : null}
                {GOOGLE_PLAY_URL ? (
                  <a
                    href={GOOGLE_PLAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="waitlist-googleplay-link"
                    className="text-xs text-zinc-500 hover:text-neon-cyan transition-colors"
                  >
                    Get GateMate on Google Play →
                  </a>
                ) : null}
              </div>
            </div>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
